import { Keyboard, View } from 'react-native'
import { useMutation } from '@apollo/client'
import { useRouter } from 'expo-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { USER_MODIFY_PASSWORD } from '@/constants/graphql/mutations'
import { Button, FormScrollView, useToast } from '@/components/base'
import { FormInput } from '@/components/form-control'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NavigationBar from '@/components/NavigationBar'

type passwordValues = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

const defaultValues: passwordValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
}

export default function ChangePasswordScreen() {
  const router = useRouter()
  const { t } = useTranslation()
  const { control, handleSubmit, setError, watch } = useForm<passwordValues>({ defaultValues })
  const loading = useLoadingIndicator()
  const toast = useToast()
  const oldPassword = watch('oldPassword')
  const newPassword = watch('newPassword')
  const confirmPassword = watch('confirmPassword')

  const [userModifyPassword] = useMutation(USER_MODIFY_PASSWORD, {
    onCompleted: (data) => {
      loading.stop()
      const isSuccess = data.userModifyPassword
      router.navigate('/(main)/(tabs)/my')
      toast.show({
        variant: isSuccess ? 'success' : 'warning',
        description: isSuccess ? t('settings:changeLoginPasswordSuccess') : t('settings:changeLoginPasswordFailed')
      })
    },
    onError: (error) => {
      loading.stop()
      const { graphQLErrors } = error
      const errorCode = graphQLErrors[0]?.extensions?.code
      if (errorCode === 3015) {
        setError('oldPassword', { message: t('auth:hint.wrongPassword') })
      }
      console.log(errorCode)
    }
  })

  const onSubmit = async ({ oldPassword, newPassword, confirmPassword }: passwordValues) => {
    Keyboard.dismiss()
    if (newPassword.length < 8 || !/\d/.test(newPassword) || !/[a-zA-Z]/.test(newPassword)) {
      setError('newPassword', { message: t('auth:hint.password') })
      return
    }
    if (newPassword !== confirmPassword) {
      setError('confirmPassword', { message: t('auth:hint.mismatch') })
      return
    }
    loading.start()
    userModifyPassword({
      variables: {
        oldPassword,
        newPassword
      }
    })
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar>{t('settings:changeLoginPassword')}</NavigationBar>
        }}
      />
      <FormScrollView className={'flex-1 bg-background-primary'} contentContainerStyle={{ flexGrow: 1 }}>
        <View className={'grow gap-4 bg-background-primary p-4'}>
          <View className={'gap-2'}>
            <FormInput
              control={control}
              name={'oldPassword'}
              label={t('settings:oldPassword')}
              type={'password'}
              autoCorrect={false}
              returnKeyType={'done'}
              placeholder={t('settings:oldPasswordPlaceholder')}
            />
            <FormInput
              control={control}
              name={'newPassword'}
              label={t('auth:label.newPassword')}
              type={'password'}
              autoCorrect={false}
              returnKeyType={'done'}
              placeholder={t('auth:placeholder.newPassword')}
              hint={t('auth:hint.password')}
            />
            <FormInput
              control={control}
              name={'confirmPassword'}
              label={t('auth:label.confirmNewPassword')}
              type={'password'}
              autoCorrect={false}
              returnKeyType={'done'}
              placeholder={t('auth:placeholder.confirmNewPassword')}
            />
          </View>
          <Button
            onPress={handleSubmit(onSubmit)}
            disabled={oldPassword === '' || newPassword === '' || confirmPassword === '' || loading.isLoading}
          >
            {t('ui:complete')}
          </Button>
          <View className={'mt-[-16px] h-4'} />
        </View>
      </FormScrollView>
    </>
  )
}
